import type { LDClient, LDContext } from 'launchdarkly-react-client-sdk';
import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import type { ReactNode } from 'react';
import { useLayoutEffect, useMemo, useRef } from 'react';

import {
  LOGGED_OUT_CONTEXT,
  forgetFlagsContext,
  saveFlagsContext,
} from '#src/lib/featureFlags.ts';
import { sessionStorage } from '#src/lib/storage/sessionStorage.ts';

import type { FeatureFlagsContextType, FlagSet } from './index.ts';
import { FeatureFlagsContext } from './index.ts';

type Props = {
  children: ReactNode;
};

export const FeatureFlagsProvider = ({ children }: Props) => {
  const client = useLDClient();
  const flags = useFlags();
  const requestedContext = useRef<string>(undefined);

  useLayoutEffect(() => {
    // @ts-expect-error Unknown global property on window
    // eslint-disable-next-line no-underscore-dangle
    window.__CLOUDFRONT_ASSETS = flags.useCloudfrontAssets;
  }, [flags.useCloudfrontAssets]);

  // Built once per client so `identify` consumers don't re-run on flag changes.
  const { identify, identifyLoggedOut } = useMemo(() => {
    // Skips the request when `context` is the last one requested, or the one
    // the client started with (see `withFFProvider`). `getContext()` alone is
    // not enough: it only changes once an `identify` request completes.
    const identifyContext = async (
      ldClient: LDClient,
      context: LDContext,
      hash?: string,
      onDone?: (err: Error | null, flags: FlagSet | null) => void
    ) => {
      const key = JSON.stringify(context);
      const lastKey =
        requestedContext.current ?? JSON.stringify(ldClient.getContext());
      if (key === lastKey) {
        const loadedFlags = ldClient.allFlags();
        onDone?.(null, loadedFlags);
        return loadedFlags;
      }
      requestedContext.current = key;
      return ldClient.identify(context, hash, onDone);
    };

    const identifyUser: FeatureFlagsContextType['identify'] = !client
      ? undefined
      : async (
          { custom = {}, privateAttributeNames, ...user },
          hash?,
          onDone?
        ) => {
          const context: LDContext = {
            kind: 'user',
            ...user,
            ...custom,
            _meta: {
              privateAttributes: privateAttributeNames,
            },
          };
          saveFlagsContext(context);
          return identifyContext(client, context, hash, onDone);
        };

    const identifySharedContext: FeatureFlagsContextType['identifyLoggedOut'] =
      !client
        ? undefined
        : async () => {
            forgetFlagsContext();
            return identifyContext(client, LOGGED_OUT_CONTEXT);
          };

    return {
      identify: identifyUser,
      identifyLoggedOut: identifySharedContext,
    };
  }, [client]);

  const value = useMemo(() => {
    const forcedFlag = sessionStorage.getItem('forcedFlag') || {};
    // avoid spreading all flags as this logs them as being still all used
    const proxiedFlags = new Proxy<typeof flags>(flags, {
      get(allFlags, prop, receiver) {
        const actual = Reflect.get(allFlags, prop, receiver);
        if (
          (Object.keys(forcedFlag) as (keyof typeof forcedFlag)[]).includes(
            prop
          )
        ) {
          return forcedFlag[prop];
        }
        return actual;
      },
    });

    const allFlags = client?.allFlags() ?? {};

    const trackedFlags = new Set(allFlags['monitored-feature-flags'] ?? []);

    const monitoredFlags = Object.fromEntries(
      Object.entries(allFlags).filter(([key]) => trackedFlags.has(key))
    );

    return {
      flags: proxiedFlags,
      monitoredFlags,
      identify,
      identifyLoggedOut,
    };
  }, [client, flags, identify, identifyLoggedOut]);

  return <FeatureFlagsContext value={value}>{children}</FeatureFlagsContext>;
};
